import { useEffect, useRef, useState } from "react";

import type {
  BannerLibraryFilterDraft,
  BannerLibraryFilterType,
  BannerLibraryTranslations,
  LoadBannerFilterOptions,
  LoadBannerPage,
  MarketingMaterial,
} from "./types";

import { useIsMobile } from "../../hooks/useIsMobile";
import { ChevronLeftIcon } from "../../Icons";
import { MarketingCard } from "../../MarketingCard";
import { ResponsiveSpinner } from "../../ResponsiveSpinner";
import { SegmentControl } from "../../SegmentControl";
import { Typo } from "../../Typo";
import { MarketingBannersEmptyDisplay } from "../MarketingBannersEmptyDisplay";
import { MarketingBannersErrorDisplay } from "../MarketingBannersErrorDisplay";
import { BannerLibraryFilter } from "./BannerLibraryFilter";
import {
  BannerLibraryGridSkeleton,
  BannerLibrarySkeleton,
} from "./BannerLibrarySkeleton";
import { EMPTY_BANNER_LIBRARY_FILTER } from "./types";
import { useBannerLibraryData } from "./useBannerLibraryData";

// Translate language codes and sort them for display: English first, then
// alphabetically by translated label.
const toSortedLanguageLabels = (
  codes: string[],
  getLabel: (code: string) => string,
): string[] =>
  [...codes]
    .sort((a, b) => {
      const aIsEn = a.toLowerCase() === "en";
      const bIsEn = b.toLowerCase() === "en";
      if (aIsEn && !bIsEn) return -1;
      if (!aIsEn && bIsEn) return 1;
      return getLabel(a).localeCompare(getLabel(b));
    })
    .map((code) => getLabel(code));

export type BannerLibraryProps = {
  translations: BannerLibraryTranslations;
  getLanguageLabelFromCode: (code: string) => string;
  loadPage: LoadBannerPage;
  // Optional dedicated loader for the complete filter dropdown values. When
  // provided, it is the source of truth for the Language/Size options instead of
  // deriving them from loaded page assets.
  loadFilterOptions?: LoadBannerFilterOptions;
  onSelectMaterial: (material: MarketingMaterial) => void;
  onBack?: () => void;
  /** Fired whenever the All/HTML/Image segmented tab changes. */
  onTabChange?: (tab: BannerLibraryFilterType) => void;
  /** Fired when the filter button is clicked, opening the modal. */
  onFilterButtonClick?: () => void;
  /** Fired for each newly-selected language filter option. */
  onFilterLanguageChange?: (option: string) => void;
  /** Fired for each newly-selected size filter option. */
  onFilterSizeChange?: (option: string) => void;
  /** Fired with the draft filter being applied, before it's committed. */
  onFilterApply?: (draft: BannerLibraryFilterDraft) => void;
  /** Fired when the filter modal's Clear button is clicked. */
  onFilterClear?: () => void;
  /** Fired when a card's language-list hover tooltip opens. */
  onLanguagesTooltipOpen?: () => void;
  /** Fired when a card's size-list hover tooltip opens. */
  onSizesTooltipOpen?: () => void;
};

export const BannerLibrary = ({
  translations: t,
  getLanguageLabelFromCode,
  loadPage,
  loadFilterOptions,
  onSelectMaterial,
  onBack,
  onTabChange,
  onFilterButtonClick,
  onFilterLanguageChange,
  onFilterSizeChange,
  onFilterApply,
  onFilterClear,
  onLanguagesTooltipOpen,
  onSizesTooltipOpen,
}: BannerLibraryProps) => {
  const isMobile = useIsMobile();

  const [activeFilter, setActiveFilter] =
    useState<BannerLibraryFilterType>("all");
  const [appliedFilters, setAppliedFilters] =
    useState<BannerLibraryFilterDraft>(EMPTY_BANNER_LIBRARY_FILTER);

  const handleResetAllFilters = () => {
    setActiveFilter("all");
    setAppliedFilters(EMPTY_BANNER_LIBRARY_FILTER);
  };

  const {
    items: materials,
    status,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    languageOptions: rawLanguageOptions,
    sizeOptions: rawSizeOptions,
  } = useBannerLibraryData({
    loadPage,
    loadFilterOptions,
    type: activeFilter,
    languages: appliedFilters.language.map((o) => o.value),
    sizes: appliedFilters.bannerSize.map((o) => o.value),
  });

  const languageOptions = rawLanguageOptions
    .map((o) => ({ value: o.value, label: getLanguageLabelFromCode(o.value) }))
    .sort((a, b) => {
      const aIsEn = a.value.toLowerCase() === "en";
      const bIsEn = b.value.toLowerCase() === "en";
      if (aIsEn && !bIsEn) return -1;
      if (!aIsEn && bIsEn) return 1;
      return a.label.localeCompare(b.label);
    });
  const sizeOptions = [...rawSizeOptions].sort((a, b) =>
    a.value.localeCompare(b.value),
  );

  const isLoading = status === "pending";
  const isError = status === "error";
  const isEmpty = status === "success" && materials.length === 0;

  // Track whether data has ever loaded so a filter change shows a grid-only
  // skeleton rather than replacing the whole page. State, not a ref, since
  // this directly decides which of the two skeletons renders. `hasLoaded`
  // only ever flips false -> true, so it's adjusted directly during render
  // (no effect needed) rather than via a set-state-in-effect.
  const [hasLoaded, setHasLoaded] = useState(status === "success");
  if (status === "success" && !hasLoaded) setHasLoaded(true);

  const isInitialLoad = isLoading && !hasLoaded;
  const isFilterLoading = isLoading && hasLoaded;

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const filterOptions: { label: string; value: BannerLibraryFilterType }[] = [
    { label: t.filters.all, value: "all" },
    { label: t.filters.html, value: "html" },
    { label: t.filters.image, value: "image" },
  ];

  if (isInitialLoad) {
    return (
      <div className="flex flex-col gap-8 bg-white lg:gap-16">
        <BannerLibrarySkeleton />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 bg-white lg:gap-16">
      <button
        type="button"
        className="flex w-fit items-center gap-2 transition-colors"
        onClick={onBack}
      >
        <ChevronLeftIcon className="size-4 shrink-0" />
        <Typo
          element="span"
          type="labelMD"
          smType="labelSM"
          textColor="text-text-primary"
        >
          {t.backButton}
        </Typo>
      </button>
      <div className="flex flex-col gap-6 lg:gap-10">
        <Typo
          type="heading4XL"
          smType="heading2XL"
          textColor="text-text-primary"
        >
          {t.title}
        </Typo>

        <div className="flex flex-col gap-2 lg:gap-10">
          <div className="-mx-4 flex items-center gap-4 bg-white p-4 lg:mx-0 lg:p-0">
            <div className="flex-1 lg:flex-none">
              <SegmentControl
                options={filterOptions}
                value={activeFilter}
                onChange={(tab) => {
                  onTabChange?.(tab);
                  setActiveFilter(tab);
                }}
                aria-label={t.filterAriaLabel}
                fullWidth={isMobile}
              />
            </div>
            <BannerLibraryFilter
              applied={appliedFilters}
              onApply={setAppliedFilters}
              languageOptions={languageOptions}
              sizeOptions={sizeOptions}
              translations={t}
              onFilterButtonClick={onFilterButtonClick}
              onLanguageChange={onFilterLanguageChange}
              onSizeChange={onFilterSizeChange}
              onApplyClick={onFilterApply}
              onClearClick={onFilterClear}
            />
          </div>

          {isFilterLoading ? (
            <BannerLibraryGridSkeleton />
          ) : isEmpty ? (
            <MarketingBannersEmptyDisplay
              title={t.empty.title}
              subtitle={t.empty.subtitle}
              onReset={handleResetAllFilters}
              resetButtonLabel={t.empty.resetButton}
            />
          ) : isError ? (
            <MarketingBannersErrorDisplay
              onRefresh={() => void refetch()}
              title={t.error.title}
              subtitle={t.error.text}
              buttonLabel={t.error.action}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10 lg:grid-cols-3 xl:grid-cols-4">
                {materials.map((material) => (
                  <MarketingCard
                    key={material.id}
                    banner={material.banner}
                    title={material.title}
                    tags={material.tags}
                    sizes={material.sizes}
                    languages={toSortedLanguageLabels(
                      material.languages,
                      getLanguageLabelFromCode,
                    )}
                    onConfigure={() => onSelectMaterial(material)}
                    onLanguagesTooltipOpen={onLanguagesTooltipOpen}
                    onSizesTooltipOpen={onSizesTooltipOpen}
                  />
                ))}
              </div>

              <div ref={sentinelRef} className="h-1" />

              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <ResponsiveSpinner height={40} width={40} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
